import React from 'react';
import styled from 'styled-components';
import { Prose } from '$components/chakra/layout/prose';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import {
  Inpage,
  InpageHeader,
  InpageHeaderInner,
  InpageHeadline,
  InpageTitle,
  InpageBody,
  InpageDescription
} from '../../styles/inpage';
import { Fold, FoldInner } from '../../styles/fold';
import { makeAbsUrl } from '../../utils/history';

const UhOhProse = styled(Prose)`
  grid-column: 1 / -1;
  text-align: center;
  align-items: center;
`;

const ErrorPageRoot = styled.div`
  min-height: 100vh;
`;

function ErrorPage({ title, description, children }) {
  return (
    <ErrorPageRoot>
      <Inpage>
        <InpageHeader>
          <InpageHeaderInner>
            <InpageHeadline centered>
              <InpageTitle>{title}</InpageTitle>
              <InpageDescription>{description}</InpageDescription>
            </InpageHeadline>
          </InpageHeaderInner>
        </InpageHeader>
        <InpageBody>
          <Fold>
            <FoldInner>
              <UhOhProse>{children}</UhOhProse>
            </FoldInner>
          </Fold>
        </InpageBody>
      </Inpage>
    </ErrorPageRoot>
  );
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const isResponseError = isRouteErrorResponse(error);
  const errorMessage = isResponseError
    ? typeof error.data === 'string'
      ? error.data
      : null
    : error instanceof Error
      ? error.message
      : null;

  return (
    <ErrorPage
      title={isResponseError ? `${error.status}` : 'UhOh'}
      description={
        isResponseError
          ? error.statusText || "That's a route error"
          : "That's a route error"
      }
    >
      <p>
        We were not able to load this page. Try refreshing the page or visit the{' '}
        <a href={makeAbsUrl('/')} title='Visit homepage'>
          OCI homepage
        </a>
        .
      </p>
      {errorMessage ? <p>{errorMessage}</p> : null}
      <p>
        If the problem persists you can reach us via{' '}
        <a href='mailto:OCIPlus@rmi.org' title='Send us an email'>
          email
        </a>
        .
      </p>
    </ErrorPage>
  );
}

export default class ErrorBoundary extends React.Component {
  static getDerivedStateFromError(error) {
    return { error: error };
  }

  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  render() {
    return this.state.error ? (
      <ErrorPage title='UhOh' description="That's a fatal error">
        <p>
          Something went wrong and we were not able to fulfill your request.
          This is on our side and we&apos;ll fix it.
        </p>
        <p>
          In the meantime you can try again by refreshing the page or visit the{' '}
          <a href={makeAbsUrl('/')} title='Visit homepage'>
            OCI homepage
          </a>
          .
        </p>
        <p>
          If this error keeps happening you can reach us via{' '}
          <a href='mailto:OCIPlus@rmi.org' title='Send us an email'>
            email
          </a>
          .
        </p>
      </ErrorPage>
    ) : (
      // eslint-disable-next-line react/prop-types
      this.props.children
    );
  }
}
